// سجل الإدارات — Department ID + الاسم الرسمي + الأسماء البديلة
import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import api from "@/lib/api";
import { errText } from "@/v2/lib/hooks";
import { GlassCard, Chip, Skeleton, Modal, Table, PageHead, Confirm, Empty } from "@/v2/admin/ui";
import { ROLE_AR } from "@/v2/lib/rbac";
import {
  Building2, Plus, Tags, Users, Trash2, Power, Pencil, X, Check,
} from "lucide-react";

function AliasModal({ dept, onClose }) {
  const qc = useQueryClient();
  const [alias, setAlias] = useState("");
  const [list, setList] = useState(dept.aliases || []);
  const [busy, setBusy] = useState(false);

  const add = async () => {
    if (alias.trim().length < 3) return toast.error("اكتب الاسم البديل");
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/departments/${dept.id}/aliases`, { alias: alias.trim() });
      setList(data.aliases); setAlias("");
      qc.invalidateQueries({ queryKey: ["admin-depts"] });
      toast.success("تمت إضافة الاسم البديل");
    } catch (e) { toast.error(errText(e)); } finally { setBusy(false); }
  };
  const remove = async (a) => {
    try {
      const { data } = await api.post(`/admin/departments/${dept.id}/aliases/remove`, { alias: a });
      setList(data.aliases);
      qc.invalidateQueries({ queryKey: ["admin-depts"] });
    } catch (e) { toast.error(errText(e)); }
  };

  return (
    <Modal title={`الأسماء البديلة — ${dept.name}`} onClose={onClose} testId="alias-modal">
      <p className="text-xs text-ink-2 mb-4 leading-relaxed">
        تساعد الأسماء البديلة النظام على التعرف على الإدارة عند اختلاف طريقة الكتابة أو وجود أخطاء إملائية بسيطة.
      </p>
      <div className="flex gap-2 mb-4">
        <input value={alias} onChange={(e) => setAlias(e.target.value)} data-testid="alias-input"
          placeholder="مثال: اكاديمية تطوير الموارد البشريه" className="v2-input" />
        <button onClick={add} disabled={busy} data-testid="alias-add" className="v2-btn v2-btn-sm shrink-0"><Plus size={15} /> إضافة</button>
      </div>
      <div className="space-y-2 max-h-60 overflow-y-auto" data-testid="alias-list">
        {list.length === 0 && <p className="text-sm text-ink-2">لا توجد أسماء بديلة بعد.</p>}
        {list.map((a) => (
          <div key={a} className="flex items-center gap-2 v2-inset px-3 py-2">
            <Tags size={14} className="text-beige-ink" />
            <span className="flex-1 text-sm">{a}</span>
            <button onClick={() => remove(a)} data-testid={`alias-del-${a}`} className="text-danger hover:opacity-70"><X size={15} /></button>
          </div>
        ))}
      </div>
    </Modal>
  );
}

function EmployeesModal({ dept, onClose }) {
  const { data, isLoading } = useQuery({
    queryKey: ["dept-emps", dept.id],
    queryFn: async () => (await api.get(`/admin/departments/${dept.id}/employees`)).data,
  });
  return (
    <Modal title={`موظفو ${dept.name}`} onClose={onClose} wide testId="dept-emps-modal">
      {isLoading ? <Skeleton className="h-48 w-full" /> : (data?.rows || []).length === 0 ? (
        <p className="text-sm text-ink-2">لا يوجد موظفون في هذه الإدارة.</p>
      ) : (
        <div className="space-y-2 max-h-[60vh] overflow-y-auto">
          {data.rows.map((r) => (
            <div key={r.id} className="flex items-center gap-3 v2-inset p-3">
              <div className="flex-1 min-w-0">
                <div className="font-bold text-sm truncate">{r.name}</div>
                <div className="text-xs text-ink-2">{r.job_title || "—"} · {r.section || "—"}</div>
              </div>
              <Chip tone={r.role === "dept_manager" ? "gold" : "muted"}>{ROLE_AR[r.role] || r.role}</Chip>
            </div>
          ))}
        </div>
      )}
    </Modal>
  );
}

export default function Departments() {
  const qc = useQueryClient();
  const { data = [], isLoading } = useQuery({ queryKey: ["admin-depts"], queryFn: async () => (await api.get("/admin/departments")).data });
  const [creating, setCreating] = useState(false);
  const [name, setName] = useState("");
  const [editing, setEditing] = useState(null);
  const [editName, setEditName] = useState("");
  const [aliasFor, setAliasFor] = useState(null);
  const [empsFor, setEmpsFor] = useState(null);
  const [delTarget, setDelTarget] = useState(null);

  const refresh = () => qc.invalidateQueries({ queryKey: ["admin-depts"] });

  const create = async () => {
    if (name.trim().length < 3) return toast.error("اكتب اسم الإدارة");
    try {
      await api.post("/admin/departments", { name: name.trim() });
      toast.success("تم إنشاء الإدارة"); setName(""); setCreating(false); refresh();
    } catch (e) { toast.error(errText(e)); }
  };
  const patch = async (id, body, msg) => {
    try { await api.patch(`/admin/departments/${id}`, body); toast.success(msg); refresh(); }
    catch (e) { toast.error(errText(e)); }
  };
  const remove = async () => {
    try {
      await api.delete(`/admin/departments/${delTarget.id}`);
      toast.success("تم حذف الإدارة"); setDelTarget(null); refresh();
    } catch (e) { toast.error(errText(e)); setDelTarget(null); }
  };

  return (
    <div className="space-y-5" data-testid="admin-departments">
      <PageHead icon={Building2} title="الإدارات" sub="الإدارات وموظفوها.">
        <button onClick={() => setCreating(true)} data-testid="dept-create-btn" className="v2-btn v2-btn-sm"><Plus size={15} /> إدارة جديدة</button>
      </PageHead>

      {isLoading ? <Skeleton className="h-64 w-full" /> : data.length === 0 ? <Empty text="لا توجد إدارات بعد." /> : (
        <Table head={["المعرّف", "الاسم الرسمي", "الموظفون", "مدير الإدارة", "الأسماء البديلة", "الحالة", ""]} min={880} testId="dept-table">
          {data.map((d) => (
            <tr key={d.id} data-testid="dept-row">
              <td className="font-mono text-xs v2-gold-text">{d.code}</td>
              <td>
                {editing === d.id ? (
                  <div className="flex gap-2">
                    <input value={editName} onChange={(e) => setEditName(e.target.value)} data-testid="dept-edit-input" className="v2-input !py-1.5 text-sm" />
                    <button onClick={() => { patch(d.id, { name: editName }, "تم تحديث الاسم"); setEditing(null); }}
                      data-testid="dept-edit-save" className="v2-btn v2-btn-sm"><Check size={14} /></button>
                    <button onClick={() => setEditing(null)} className="v2-btn-ghost v2-btn-sm"><X size={14} /></button>
                  </div>
                ) : (
                  <span className="font-semibold flex items-center gap-2">
                    {d.name}
                    <button onClick={() => { setEditing(d.id); setEditName(d.name); }} data-testid={`dept-edit-${d.code}`}
                      className="text-ink-2 hover:text-ink"><Pencil size={13} /></button>
                  </span>
                )}
              </td>
              <td>
                <button onClick={() => setEmpsFor(d)} data-testid={`dept-emps-${d.code}`} className="inline-flex items-center gap-1 text-sm hover:text-beige-ink">
                  <Users size={14} /> {d.employees_count}
                </button>
              </td>
              <td className="text-ink-2 text-sm">{d.manager || "—"}</td>
              <td>
                <button onClick={() => setAliasFor(d)} data-testid={`dept-alias-${d.code}`} className="v2-btn-ghost v2-btn-sm">
                  <Tags size={13} /> {(d.aliases || []).length}
                </button>
              </td>
              <td><Chip tone={d.active !== false ? "emerald" : "muted"}>{d.active !== false ? "مفعّلة" : "معطّلة"}</Chip></td>
              <td>
                <div className="flex gap-2">
                  <button onClick={() => patch(d.id, { active: d.active === false }, d.active === false ? "تم التفعيل" : "تم التعطيل")}
                    data-testid={`dept-toggle-${d.code}`} className="v2-btn-ghost v2-btn-sm"><Power size={13} /></button>
                  <button onClick={() => setDelTarget(d)} data-testid={`dept-del-${d.code}`} className="v2-btn-ghost v2-btn-sm text-danger"><Trash2 size={13} /></button>
                </div>
              </td>
            </tr>
          ))}
        </Table>
      )}

      {creating && (
        <Modal title="إدارة جديدة" onClose={() => setCreating(false)} testId="dept-create-modal">
          <label className="v2-label">الاسم الرسمي للإدارة</label>
          <input value={name} onChange={(e) => setName(e.target.value)} data-testid="dept-name-input" className="v2-input mb-4" />
          <p className="text-xs text-ink-2 mb-5">سيتم منح الإدارة معرّفًا فريدًا تلقائيًا (DEP-00X)، ولن يُسمح بتكرار اسم موجود.</p>
          <button onClick={create} data-testid="dept-create-save" className="v2-btn w-full">إنشاء الإدارة</button>
        </Modal>
      )}
      {aliasFor && <AliasModal dept={aliasFor} onClose={() => setAliasFor(null)} />}
      {empsFor && <EmployeesModal dept={empsFor} onClose={() => setEmpsFor(null)} />}
      {delTarget && (
        <Confirm title="حذف الإدارة" body={`سيتم حذف «${delTarget.name}» نهائيًا. لا يمكن حذف إدارة بها موظفون.`}
          confirmLabel="حذف" danger onConfirm={remove} onClose={() => setDelTarget(null)} testId="dept-del-confirm" />
      )}
    </div>
  );
}
